import { CKEditor } from '@ckeditor/ckeditor5-react';
import { FeatureType } from '@conversed/shared/src/central/types/feature-access.type';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import {
  Bold,
  ClassicEditor,
  Essentials,
  Italic,
  Link,
  List,
  Markdown,
  Paragraph,
  Undo,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

type TemplateOption = {
  value: string;
  description: string;
  content: string;
  url: string;
};

// Two visual styles for the "/" slash-command template dropdown.
// 'default' — heavier classic chrome (legacy FactCheckingModal).
// 'compact' — lighter, smaller text, subtle hover/active states; matches v2 modal aesthetic.
type SlashDropdownVariant = 'default' | 'compact';

interface IProps {
  value: string;
  onChange: (value: string) => void;
  constantValue: string;
  templateOptions?: TemplateOption[];
  onTemplateSelect?: (url: string) => void;
  slashDropdownVariant?: SlashDropdownVariant;
}

export interface AnswerEditorHandle {
  setEditorText: (text: string) => void;
}

export const AnswerEditor = forwardRef<AnswerEditorHandle, IProps>(
  (
    {
      value,
      onChange,
      constantValue,
      templateOptions,
      onTemplateSelect,
      slashDropdownVariant = 'default',
    },
    ref,
  ) => {
    const { hasFeatureAccess } = useFeatureAccess();

    // * Slash command template insertion is only available for Medior and Senior packages.
    // * Junior clients receive an empty list so the "/" trigger never opens the dropdown.
    const effectiveTemplateOptions = useMemo(
      () =>
        hasFeatureAccess(FeatureType.SLASH_COMMAND_TEMPLATES)
          ? (templateOptions ?? [])
          : [],
      [hasFeatureAccess, templateOptions],
    );

    const [editorText, setEditorText] = useState(value);
    const [constantField, setConstantField] = useState('');

    // Holds the CKEditor-normalized form of whatever content was last pushed into
    // the editor (initial value, prop-driven sync, or imperative setEditorText).
    // CKEditor can re-format the HTML it receives (whitespace, attribute order, etc.),
    // so comparing onBlur output against the raw input we sent is unreliable. We
    // capture editor.getData() right after each set and compare against THAT - so
    // a focus+blur with no actual edit produces an identical string and we skip
    // the parent's onChange (which would otherwise dirty the draft for no reason).
    const baselineRef = useRef<string>(value);

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [dropdownPosition, setDropdownPosition] = useState({
      top: 0,
      left: 0,
    });

    const editorRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const activeItemRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // * Refs to avoid stale closures inside the native keydown listener
    const dropdownOpenRef = useRef(false);
    const searchQueryRef = useRef('');
    const activeIndexRef = useRef(0);
    const filteredRef = useRef<TemplateOption[]>([]);

    const updateDropdown = useCallback(
      (open: boolean, query: string, index: number) => {
        dropdownOpenRef.current = open;
        searchQueryRef.current = query;
        activeIndexRef.current = index;
        setDropdownOpen(open);
        setSearchQuery(query);
        setActiveIndex(index);
      },
      [],
    );

    // * clearDropdown resets the search query and active index while keeping
    // * the dropdown visible (e.g. after positioning is computed).
    // * clearAndCloseDropdown does the same but also closes the dropdown.
    const clearDropdown = useCallback(
      () => updateDropdown(true, '', 0),
      [updateDropdown],
    );

    const clearAndCloseDropdown = useCallback(
      () => updateDropdown(false, '', 0),
      [updateDropdown],
    );

    const filteredOptions = useMemo(
      () =>
        effectiveTemplateOptions.filter((template) =>
          template.description
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
        ),
      [effectiveTemplateOptions, searchQuery],
    );
    filteredRef.current = filteredOptions;

    const insertTemplate = useCallback(
      (content: string, query: string, templateUrl: string) => {
        const editor = editorRef.current;
        if (!editor) {
          return;
        }

        editor.model.change((writer: any) => {
          const selection = editor.model.document.selection;
          const focus = selection.focus;
          const charsToRemove = query.length + 1; // * "/" + query
          const start = focus.getShiftedBy(-charsToRemove);
          writer.remove(writer.createRange(start, focus));
        });

        const wrapped = `<p>&nbsp;</p>${content}<p>&nbsp;</p>`;
        const viewFragment = editor.data.processor.toView(wrapped);
        const modelFragment = editor.data.toModel(viewFragment);
        editor.model.insertContent(modelFragment);

        // * Return focus to the editor, then scroll the nearest scrollable
        // * ancestor (the modal body) to reveal the cursor.
        // * scrollToTheSelection() only scrolls the window, which is ineffective
        // * when the editor sits inside an overflow:auto modal container.
        // * We defer via setTimeout so CKEditor has time to sync the model
        // * selection back to a DOM selection before we read it.
        editor.editing.view.focus();
        setTimeout(() => {
          const domSelection = window.getSelection();
          if (domSelection?.rangeCount) {
            const range = domSelection.getRangeAt(0);
            const cursorNode = range.startContainer;
            const cursorElement =
              cursorNode.nodeType === Node.ELEMENT_NODE
                ? (cursorNode as Element)
                : cursorNode.parentElement;
            cursorElement?.scrollIntoView({ block: 'nearest' });
          }
        }, 0);

        // * Always fire onTemplateSelect regardless of whether a URL is present,
        // * matching Template Selector parity — an empty URL clears the field.
        onTemplateSelect?.(templateUrl ?? '');
        clearAndCloseDropdown();
      },
      [clearAndCloseDropdown, onTemplateSelect],
    );

    useEffect(() => {
      if (constantField !== constantValue) {
        setConstantField(constantValue);
        setEditorText(value);
        clearAndCloseDropdown();
      }
    }, [value, constantValue, constantField, clearAndCloseDropdown]);

    // After every editorText change (initial mount, prop sync, imperative
    // setEditorText, or post-blur setEditorText), refresh baselineRef with
    // CKEditor's normalized output so the next blur can detect a true no-op.
    // editorText is used as a trigger dep, not read in the body.
    // biome-ignore lint/correctness/useExhaustiveDependencies: editorText is a trigger dependency
    useEffect(() => {
      if (editorRef.current) {
        baselineRef.current =
          editorRef.current.getData()?.replace(/\\_/g, '_') ?? '';
      }
    }, [editorText]);

    // * Auto-scroll active item into view when navigating.
    // * activeIndex is intentionally used as a trigger rather than a value read
    // * inside the effect body — the ref's .current is updated via the ref prop
    // * before the effect runs, so we depend on the index change to re-fire it.
    // biome-ignore lint/correctness/useExhaustiveDependencies: activeIndex is a trigger dependency
    useEffect(() => {
      activeItemRef.current?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    // * Close dropdown on outside click.
    useEffect(() => {
      if (!dropdownOpen) {
        return;
      }

      const handler = (mouseEvent: MouseEvent) => {
        const clickTarget = mouseEvent.target as Node;
        // * The portal is rendered into document.body, so it is outside
        // * containerRef. Check both to avoid closing when the user clicks
        // * the dropdown's own scrollbar or items.
        const isInsideEditor = containerRef.current?.contains(clickTarget);
        const isInsideDropdown = dropdownRef.current?.contains(clickTarget);
        if (!isInsideEditor && !isInsideDropdown) {
          clearAndCloseDropdown();
        }
      };

      document.addEventListener('mousedown', handler);

      return () => {
        document.removeEventListener('mousedown', handler);
      };
    }, [dropdownOpen, clearAndCloseDropdown]);

    // * Close the dropdown on scroll or resize — the fixed-position portal does
    // * not move with the cursor when the layout shifts.
    // * Scroll uses capture phase to catch events from nested scrollable elements
    // * (e.g. the modal body) before they reach the document root.
    useEffect(() => {
      if (!dropdownOpen) {
        return;
      }

      const handleScroll = (scrollEvent: Event) => {
        // * Ignore scroll events that originate inside the dropdown itself —
        // * the capture-phase listener would otherwise close the dropdown
        // * when the user scrolls its own overflow list.
        if (dropdownRef.current?.contains(scrollEvent.target as Node)) {
          return;
        }

        clearAndCloseDropdown();
      };

      window.addEventListener('resize', clearAndCloseDropdown);
      document.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('resize', clearAndCloseDropdown);
        document.removeEventListener('scroll', handleScroll, true);
      };
    }, [dropdownOpen, clearAndCloseDropdown]);

    useImperativeHandle(
      ref,
      () => ({
        setEditorText: (text: string) => {
          setEditorText(text);
        },
      }),
      [],
    );

    return (
      <div className='no-resize border-none relative' ref={containerRef}>
        <CKEditor
          editor={ClassicEditor}
          data={editorText}
          config={{
            plugins: [
              Bold,
              Essentials,
              Italic,
              Link,
              List,
              Markdown,
              Paragraph,
              Undo,
            ],
            // * CKEditor v42+ requires a licenseKey. 'GPL' enables all features we
            // * use under the open-source license. A commercial key is needed only
            // * for premium features (Track Changes, Collaboration, etc.).
            // * TODO: confirm whether a commercial CKEditor license is required.
            licenseKey: 'GPL',
            toolbar: [
              'link',
              '|',
              'bulletedList',
              'numberedList',
              '|',
              'undo',
              'redo',
            ],
            link: {
              addTargetToExternalLinks: true,
              defaultProtocol: 'https://',
            },
          }}
          onReady={(editor) => {
            editorRef.current = editor;
            // Snapshot the editor's normalized form of the initial content as
            // our blur baseline (see baselineRef declaration for why).
            baselineRef.current = editor.getData()?.replace(/\\_/g, '_') ?? '';

            // * Close the dropdown when the user clicks inside the editor to
            // * reposition the cursor. change:selection is not used here because
            // * it also fires on every character typed, which would close the
            // * dropdown while the user is actively searching.
            editor.editing.view.document.on('mousedown', () => {
              if (dropdownOpenRef.current) {
                clearAndCloseDropdown();
              }
            });

            // * keydown handles only non-printable keys. Printable character
            // * detection (the "/" trigger and search accumulation) lives in the
            // * beforeinput listener below, because mobile virtual keyboards fire
            // * keydown with key="Unidentified" for all printable characters.
            editor.editing.view.document.on(
              'keydown',
              (
                editorEvent: { stop: () => void },
                eventData: { domEvent: KeyboardEvent },
              ) => {
                const { key } = eventData.domEvent;
                const isOpen = dropdownOpenRef.current;

                if (!isOpen) {
                  return;
                }

                // * Disable horizontal cursor movement while the dropdown is open.
                // * Since ArrowUp and ArrowDown are responsible with selection, we banned
                // * these keys so that the caret is guaranteed to stay in place and the text
                // * is inserted into the correct place
                if (key === 'ArrowLeft' || key === 'ArrowRight') {
                  eventData.domEvent.preventDefault();
                  editorEvent.stop();
                  return;
                }

                if (key === 'ArrowDown') {
                  // * If there are no filtered templates then the dropdown is considered closed
                  // * and we just need to clear it
                  if (filteredRef.current.length === 0) {
                    clearAndCloseDropdown();
                    return;
                  }

                  eventData.domEvent.preventDefault();
                  editorEvent.stop();
                  const next = Math.min(
                    activeIndexRef.current + 1,
                    filteredRef.current.length - 1,
                  );
                  activeIndexRef.current = next;
                  setActiveIndex(next);
                  return;
                }

                if (key === 'ArrowUp') {
                  // * If there are no filtered templates then the dropdown is considered closed
                  // * and we just need to clear it
                  if (filteredRef.current.length === 0) {
                    clearAndCloseDropdown();
                    return;
                  }

                  eventData.domEvent.preventDefault();
                  editorEvent.stop();
                  const prev = Math.max(activeIndexRef.current - 1, 0);
                  activeIndexRef.current = prev;
                  setActiveIndex(prev);
                  return;
                }

                if (key === 'Enter' || key === 'Tab') {
                  const filtered = filteredRef.current;
                  if (filtered.length > 0) {
                    eventData.domEvent.preventDefault();
                    editorEvent.stop();
                    const selected =
                      filtered[activeIndexRef.current] ?? filtered[0];
                    insertTemplate(
                      selected.content,
                      searchQueryRef.current,
                      selected.url,
                    );
                  } else {
                    // * If there are no filtered templates then the dropdown is considered closed
                    // * and we just need to clear it
                    clearAndCloseDropdown();
                  }
                  return;
                }

                if (key === 'Escape') {
                  // * Stop the native DOM event from bubbling to the document-level
                  // * keydown listener in useModalListeners, which would close the modal.
                  // * On the first Escape the dropdown closes; on the second Escape the
                  // * dropdown is already gone so this branch is never reached, the event
                  // * propagates normally, and the modal closes as expected.
                  eventData.domEvent.stopPropagation();
                  clearAndCloseDropdown();
                  return;
                }

                if (key === 'Backspace') {
                  const currentQuery = searchQueryRef.current;
                  if (currentQuery.length === 0) {
                    clearAndCloseDropdown();
                  } else {
                    updateDropdown(true, currentQuery.slice(0, -1), 0);
                  }
                  return;
                }
              },
            );

            // * beforeinput fires reliably on both desktop and mobile for all
            // * printable character insertions, unlike keydown which returns
            // * key="Unidentified" for printable keys on mobile virtual keyboards.
            // * This listener owns all "/" trigger and search-query accumulation logic.
            const editableRoot = editor.editing.view.getDomRoot();

            if (editableRoot) {
              const handleBeforeInput = (inputEvent: InputEvent) => {
                const { inputType, data: inputData } = inputEvent;

                if (inputType !== 'insertText' || !inputData) {
                  return;
                }

                if (inputData === '/') {
                  if (dropdownOpenRef.current) {
                    // * If the dropdown is already open, treat '/' as a regular
                    // * search character rather than re-triggering the open logic,
                    // * which would silently reset the search query.
                    const appendedQuery = searchQueryRef.current + inputData;
                    searchQueryRef.current = appendedQuery;
                    setSearchQuery(appendedQuery);
                    setActiveIndex(0);
                    activeIndexRef.current = 0;
                  } else {
                    // * Only open the dropdown if there are templates to show.
                    // * Use filteredRef rather than the captured templateOptions prop
                    // * to avoid reading a stale closure value from mount time.
                    if (!filteredRef.current.length) {
                      return;
                    }

                    // * Defer until after "/" is inserted into the DOM so the
                    // * view selection reflects the caret's new position.
                    requestAnimationFrame(() => {
                      let top = 0;
                      let left = 0;

                      // * Use CKEditor's domConverter to get the exact DOM node and
                      // * offset for the cursor, then measure via getClientRects().
                      // * getBoundingClientRect() on a collapsed range always returns
                      // * a zero-size rect; getClientRects()[0] returns the actual
                      // * line-level caret rect even for a collapsed selection.
                      const focusViewPosition =
                        editor.editing.view.document.selection.focus;

                      if (focusViewPosition) {
                        const domPosition =
                          editor.editing.view.domConverter.viewPositionToDom(
                            focusViewPosition,
                          );

                        if (domPosition) {
                          try {
                            const measureRange = document.createRange();
                            measureRange.setStart(
                              domPosition.parent as Node,
                              domPosition.offset,
                            );
                            measureRange.collapse(true);

                            const clientRects = measureRange.getClientRects();

                            if (clientRects.length > 0) {
                              top = clientRects[0].bottom;
                              left = clientRects[0].left;
                            } else {
                              // * No client rects — cursor is at the edge of an
                              // * element; fall back to the parent element's bounds.
                              const parentNode = domPosition.parent as Node;
                              const parentElement =
                                parentNode.nodeType === Node.ELEMENT_NODE
                                  ? (parentNode as Element)
                                  : parentNode.parentElement;
                              const parentRect =
                                parentElement?.getBoundingClientRect();

                              if (parentRect) {
                                top = parentRect.bottom;
                                left = parentRect.left;
                              }
                            }
                          } catch {
                            // * Last-resort fallback: anchor below the editable root
                            const fallbackRect =
                              editableRoot.getBoundingClientRect();
                            top = fallbackRect.bottom;
                            left = fallbackRect.left;
                          }
                        }
                      }

                      setDropdownPosition({ top, left });
                      clearDropdown();
                    });
                  }
                  return;
                }

                if (!dropdownOpenRef.current) {
                  return;
                }

                if (inputData === ' ') {
                  clearAndCloseDropdown();
                  return;
                }

                // * Accumulate the typed character into the live search query.
                updateDropdown(true, searchQueryRef.current + inputData, 0);
              };

              editableRoot.addEventListener('beforeinput', handleBeforeInput);

              // * Clean up the native listener when CKEditor destroys the editor
              // * instance (e.g. when the component unmounts).
              editor.on('destroy', () => {
                editableRoot.removeEventListener(
                  'beforeinput',
                  handleBeforeInput,
                );
              });
            }
          }}
          onBlur={(_event, editor) => {
            const text = editor.getData()?.replace(/\\_/g, '_');
            // Skip the parent's onChange when the user blurred without making
            // any actual edits - baselineRef holds the CKEditor-normalized form
            // from the last set, so comparing against it (rather than the raw
            // value prop) avoids false positives from CKEditor's HTML
            // normalization.
            if (text === baselineRef.current) {
              return;
            }
            baselineRef.current = text;
            onChange(text);
            setEditorText(text);
          }}
        />
        {dropdownOpen &&
          filteredOptions.length > 0 &&
          // * Portal into document.body so the dropdown escapes any ancestor
          // * with a CSS transform (the modal uses translate), which would
          // * otherwise break position:fixed viewport-relative coordinates.
          createPortal(
            <div
              ref={dropdownRef}
              className={
                slashDropdownVariant === 'compact'
                  ? 'fixed z-[9999] w-60 bg-white rounded-lg shadow-lg ring-1 ring-gray-200 max-h-44 overflow-y-auto overflow-x-hidden divide-y divide-gray-100'
                  : 'fixed z-[9999] w-56 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto'
              }
              style={{
                top: dropdownPosition.top + 4,
                left: dropdownPosition.left,
              }}>
              {filteredOptions.map((option, index) => {
                const isActive = index === activeIndex;
                const itemClassName =
                  slashDropdownVariant === 'compact'
                    ? `px-2.5 py-1.5 text-tiny cursor-pointer font-normal break-all ${
                        isActive
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`
                    : `px-3 py-2 text-sm cursor-pointer ${
                        isActive
                          ? 'bg-primary-base text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`;
                return (
                  <div
                    key={option.value}
                    ref={isActive ? activeItemRef : null}
                    onMouseDown={(mouseEvent) => {
                      mouseEvent.preventDefault();
                      insertTemplate(option.content, searchQuery, option.url);
                    }}
                    className={itemClassName}>
                    {option.description}
                  </div>
                );
              })}
            </div>,
            document.body,
          )}
      </div>
    );
  },
);
